/**
 * Movie rating dialog component
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import StarRating from './StarRating';
import apiService, { MovieSearchResult } from '../services/api';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Dialog = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
`;

const Title = styled.h2`
  color: #333;
  margin: 0;
  flex: 1;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0;
  margin-left: 1rem;

  &:hover {
    color: #333;
  }
`;

const MovieInfo = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const MoviePoster = styled.div<{ $posterUrl?: string }>`
  width: 120px;
  height: 180px;
  background: ${props => props.$posterUrl 
    ? `url(https://image.tmdb.org/t/p/w300${props.$posterUrl}) center/cover`
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  };
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2rem;
  flex-shrink: 0;
`;

const MovieDetails = styled.div`
  flex: 1;
`;

const MovieTitle = styled.h3`
  color: #333;
  margin: 0 0 0.5rem 0;
`;

const MovieMeta = styled.div`
  color: #666;
  margin-bottom: 1rem;
`;

const MovieOverview = styled.p`
  color: #555;
  font-size: 0.9rem;
  line-height: 1.4;
  margin: 0;
`;

const RatingSection = styled.div`
  text-align: center;
  margin: 2rem 0;
`;

const RatingLabel = styled.div`
  font-size: 1.1rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 1rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const ReasonSection = styled.div`
  margin: 1rem 0;
`;

const ReasonLabel = styled.div`
  font-size: 0.9rem;
  color: #555;
  margin-bottom: 0.5rem;
`;

const ReasonChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const ReasonChip = styled.button<{ $selected: boolean }>`
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  border: 1px solid ${p => p.$selected ? '#667eea' : '#d1d5db'};
  background: ${p => p.$selected ? '#f0f0ff' : 'white'};
  color: ${p => p.$selected ? '#667eea' : '#444'};
  font-size: 0.82rem;
  font-weight: ${p => p.$selected ? '600' : '400'};
  cursor: pointer;
  transition: all 0.15s;

  &:hover { background: #f0f0ff; border-color: #667eea; color: #667eea; }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.3s;

  ${props => props.$variant === 'primary' ? `
    background: #667eea;
    color: white;
    
    &:hover {
      background: #5a67d8;
    }
    
    &:disabled {
      background: #a0aec0;
      cursor: not-allowed;
    }
  ` : `
    background: #f8f9fa;
    color: #333;
    border: 1px solid #e1e5e9;
    
    &:hover {
      background: #e9ecef;
    }
  `}
`;

const ErrorMessage = styled.div`
  background: #fed7d7;
  color: #c53030;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

const SuccessMessage = styled.div`
  background: #c6f6d5;
  color: #2d7d32;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  text-align: center;
`;

interface MovieRatingDialogProps {
  movie: MovieSearchResult;
  isOpen: boolean;
  onClose: () => void;
  onRatingSubmitted?: () => void;
}

const MovieRatingDialog: React.FC<MovieRatingDialogProps> = ({
  movie,
  isOpen,
  onClose,
  onRatingSubmitted
}) => {
  const [rating, setRating] = useState(0);
  const [ratingReason, setRatingReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const LOW_RATING_REASONS = ['Bad acting', 'Boring plot', 'Too long', 'Predictable', 'Not my taste'];

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get additional movie details if needed
      const movieDetails = await apiService.getMovieDetails(movie.tmdb_id);
      
      const ratingData = {
        title: movie.title,
        tmdb_id: movie.tmdb_id,
        user_rating: rating,
        rating_reason: ratingReason || undefined,
        release_date: movie.release_date,
        tmdb_genres: movie.genre_ids,
        // Add more fields from movieDetails if available
        runtime: movieDetails?.runtime,
        director_name: movieDetails?.credits?.crew?.find((c: any) => c.job === 'Director')?.name,
        director_id: movieDetails?.credits?.crew?.find((c: any) => c.job === 'Director')?.id,
        cast_names: movieDetails?.credits?.cast?.slice(0, 5).map((c: any) => c.name).join(', '),
        cast_ids: movieDetails?.credits?.cast?.slice(0, 5).map((c: any) => c.id),
      };

      await apiService.rateMovie(movie.tmdb_id, ratingData);
      setSuccess(true);
      
      setTimeout(() => {
        onRatingSubmitted?.();
        onClose();
        setSuccess(false);
        setRating(0);
      }, 1500);

    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit rating. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) return;
    setRating(0);
    setRatingReason('');
    setError('');
    setSuccess(false);
    onClose();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).getFullYear();
  };

  return (
    <Overlay onClick={handleClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Rate Movie</Title>
          <CloseButton onClick={handleClose} disabled={loading}>
            ✕
          </CloseButton>
        </Header>

        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>Rating submitted successfully!</SuccessMessage>}

        <MovieInfo>
          <MoviePoster $posterUrl={movie.poster_path}>
            {!movie.poster_path && ''}
          </MoviePoster>
          <MovieDetails>
            <MovieTitle>{movie.title}</MovieTitle>
            <MovieMeta>
              {formatDate(movie.release_date)} | ⭐ {movie.vote_average?.toFixed(1)} |{' '}
              {movie.vote_count?.toLocaleString()} votes
            </MovieMeta>
            <MovieOverview>
              {movie.overview || 'No description available.'}
            </MovieOverview>
          </MovieDetails>
        </MovieInfo>

        <RatingSection>
          <RatingLabel>How would you rate this movie?</RatingLabel>
          <StarRating
            rating={rating}
            onRatingChange={(r) => { setRating(r); if (r > 2) setRatingReason(''); }}
            size="large"
          />
        </RatingSection>

        {rating > 0 && rating <= 2 && (
          <ReasonSection>
            <ReasonLabel>What didn't you like? (optional)</ReasonLabel>
            <ReasonChips>
              {LOW_RATING_REASONS.map(reason => (
                <ReasonChip
                  key={reason}
                  $selected={ratingReason === reason}
                  onClick={() => setRatingReason(prev => prev === reason ? '' : reason)}
                >
                  {reason}
                </ReasonChip>
              ))}
            </ReasonChips>
          </ReasonSection>
        )}

        <ButtonGroup>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            $variant="primary" 
            onClick={handleSubmit} 
            disabled={loading || rating === 0}
          >
            {loading ? 'Submitting...' : 'Submit Rating'}
          </Button>
        </ButtonGroup>
      </Dialog>
    </Overlay>
  );
};

export default MovieRatingDialog;